import { useEffect, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
axios.defaults.withCredentials = true;
import { LockKeyhole, LogIn, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import HandoffNotifier from "@/components/HandoffNotifier";
import { API } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const resetLabel = (busy, invite) => {
  if (busy) return "Salvataggio…";
  return invite ? "Attiva account" : "Reimposta password";
};

const submitLabel = (busy, setup) => {
  if (busy) return "Attendi…";
  return setup ? "Imposta password ed entra" : "Entra";
};
const errText = (d) => {
  if (Array.isArray(d)) return d.map((e) => e.msg).join(" ");
  return typeof d === "string" ? d : "Errore";
};

export function AdminLoginForm() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [setup, setSetup] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!email || !password) return toast.error("Inserisci email e password");
    if (setup && password !== password2) return toast.error("Le password non coincidono");
    setBusy(true);
    try {
      const url = setup ? `${API}/auth/setup-password` : `${API}/auth/login`;
      const { data } = await axios.post(url, { email, password });
      login(data);
      toast.success(setup ? "Password impostata, benvenuto!" : "Accesso effettuato");
      navigate("/");
    } catch (e) {
      if (e.response?.status === 409) {
        setSetup(true);
        toast.info("Primo accesso: scegli la tua password");
      } else toast.error(errText(e.response?.data?.detail) || "Accesso non riuscito");
    } finally {
      setBusy(false);
    }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="space-y-4" data-testid="login-page">
        <div className="space-y-1.5">
          <Label>Email</Label>
          <Input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="nome@azienda.it" />
        </div>
        <div className="space-y-1.5">
          <Label>{setup ? "Scegli una password (min 8 caratteri)" : "Password"}</Label>
          <Input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => e.key === "Enter" && !setup && submit()} />
        </div>
        {setup && (
          <div className="space-y-1.5">
            <Label>Conferma password</Label>
            <Input data-testid="login-password2" type="password" value={password2} onChange={(e) => setPassword2(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} />
          </div>
        )}
        <Button data-testid="login-submit" onClick={submit} disabled={busy} className="w-full h-11 gap-2">
          {setup ? <KeyRound className="h-4 w-4" /> : <LogIn className="h-4 w-4" />} {submitLabel(busy, setup)}
        </Button>
        {!setup && (
          <button data-testid="forgot-password-link" onClick={() => navigate("/password-dimenticata")} className="w-full text-xs text-muted-foreground hover:text-primary underline">
            Password dimenticata?
          </button>
        )}
        <div className="flex items-center gap-3 text-xs text-muted-foreground"><div className="h-px flex-1 bg-border" />oppure<div className="h-px flex-1 bg-border" /></div>
        <Button data-testid="login-google" variant="secondary" onClick={google} className="w-full h-11 gap-2">
          <svg className="h-4 w-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1A6.6 6.6 0 0 1 5.5 12c0-.73.13-1.44.34-2.1V7.06H2.18A11 11 0 0 0 1 12c0 1.77.42 3.45 1.18 4.94l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
          Accedi con Google
        </Button>
        <p className="text-[11px] text-muted-foreground text-center flex items-center justify-center gap-1"><LockKeyhole className="h-3 w-3" /> Solo account autorizzati possono accedere.</p>
    </div>
  );
}

export default function LoginPage() {
  return <Navigate to="/login?tab=admin" replace />;
}

export function AuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const { login } = useAuth();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const sid = new URLSearchParams(location.hash.replace(/^#/, "")).get("session_id");
    axios.post(`${API}/auth/google/session`, { session_id: sid })
      .then(({ data }) => { login(data); window.history.replaceState(null, "", "/"); navigate("/", { replace: true }); })
      .catch((e) => { toast.error(errText(e.response?.data?.detail) || "Accesso Google non riuscito"); window.history.replaceState(null, "", "/login?tab=admin"); navigate("/login?tab=admin", { replace: true }); });
  }, [location.hash, login, navigate]); // eslint-disable-line react-hooks/exhaustive-deps -- esegue una sola volta il callback OAuth (guardia done.current)
  return <div className="min-h-screen bg-background grid place-items-center text-muted-foreground">Accesso in corso…</div>;
}

export function ProtectedRoute({ children, adminOnly = false, perm = null }) {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen bg-background grid place-items-center text-muted-foreground">Verifica accesso…</div>;
  if (!user) return <Navigate to="/login?tab=admin" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/" replace />;
  if (perm && user.role !== "admin" && !(user.permissions || []).includes(perm)) return <Navigate to="/" replace />;
  return <><HandoffNotifier enabled={user.role === "admin" || (user.permissions || []).includes("whatsapp")} />{children}</>;
}

export function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!email) return toast.error("Inserisci l'email");
    setBusy(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch (e) {
      toast.error(errText(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="min-h-screen bg-background grid place-items-center px-6">
      <Card className="w-full max-w-md p-8 bg-card border-border space-y-4" data-testid="forgot-page">
        <div className="text-center"><div className="inline-block rounded-2xl bg-white p-2 mb-3"><Logo className="h-12" /></div><h1 className="font-display font-black text-xl">Password dimenticata</h1></div>
        {sent ? (
          <p className="text-sm text-center" data-testid="forgot-sent">Se l'email è registrata riceverai a breve un link (valido 1 ora) per reimpostare la password.</p>
        ) : (
          <>
            <Input data-testid="forgot-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email@azienda.it" onKeyDown={(e) => e.key === "Enter" && submit()} />
            <Button data-testid="forgot-submit" onClick={submit} disabled={busy} className="w-full h-11">{busy ? "Invio…" : "Invia link di reset"}</Button>
          </>
        )}
        <button onClick={() => navigate("/login")} className="w-full text-xs text-muted-foreground hover:text-primary underline">Torna al login</button>
      </Card>
    </div>
  );
}

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const params = new URLSearchParams(useLocation().search);
  const token = params.get("token") || "";
  const invite = params.get("invite") === "1";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (pw.length < 8) return toast.error("Minimo 8 caratteri");
    if (pw !== pw2) return toast.error("Le password non coincidono");
    setBusy(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password: pw });
      toast.success("Password reimpostata: accedi con la nuova password");
      navigate("/login");
    } catch (e) {
      toast.error(errText(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="min-h-screen bg-background grid place-items-center px-6">
      <Card className="w-full max-w-md p-8 bg-card border-border space-y-4" data-testid="reset-page">
        <div className="text-center"><div className="inline-block rounded-2xl bg-white p-2 mb-3"><Logo className="h-12" /></div><h1 className="font-display font-black text-xl">{invite ? "Attiva il tuo accesso" : "Nuova password"}</h1></div>
        {invite && <p className="text-xs text-muted-foreground text-center" data-testid="invite-hint">Scegli la password per il tuo account del pannello Vivi Win.</p>}
        <Input data-testid="reset-password" type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Nuova password (min 8)" />
        <Input data-testid="reset-password2" type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} placeholder="Conferma password" onKeyDown={(e) => e.key === "Enter" && submit()} />
        <Button data-testid="reset-submit" onClick={submit} disabled={busy || !token} className="w-full h-11">{resetLabel(busy, invite)}</Button>
      </Card>
    </div>
  );
}
